import { EntryType } from '@morph-mapper/types';
import { suggestionKey } from '../store/ai-suggestions';
import type { Suggestion, StoredSuggestion } from '../store/ai-suggestions';

export { suggestionKey };

export type IteratorStrategy = 'table' | 'countOccurrence';

const COLUMN_PREFIX = /^c\d+:\s*/i;

const normalize = (value: unknown) =>
  String(value).replace(COLUMN_PREFIX, '').trim();

/**
 * Whether a rendered result still produces the suggestion's value. A render
 * inside an iterator returns one result per iteration; one match is enough.
 */
export const rendersSuggestionValue = (
  rendered: unknown,
  value: unknown,
): boolean => {
  if (Array.isArray(rendered)) {
    return rendered.some((item) => rendersSuggestionValue(item, value));
  }
  if (rendered === undefined || rendered === null) return false;
  return normalize(rendered) === normalize(value);
};

/**
 * Combines a fresh evaluation with what a field already had:
 * - every fetched suggestion, keeping the applied flag of a matching one;
 * - earlier suggestions that were applied, or that still render to their
 *   value on the current document (`stillValid`).
 * Earlier suggestions that are neither are dropped, e.g. values from a
 * previous document. The result is ranked best first, wherever the new
 * suggestions end up.
 *
 * With `markNewest` (a re-evaluation) the suggestions it found are marked
 * `latest`; otherwise the marks of an earlier re-evaluation are kept.
 */
export const mergeSuggestions = (
  previous: StoredSuggestion[],
  fetched: Suggestion[],
  stillValid: Set<string>,
  markNewest = false,
): StoredSuggestion[] => {
  const previousFound = new Map(
    previous.map((s) => [suggestionKey(s), s.found] as const),
  );
  const appliedKeys = new Set(
    previous.filter((s) => s.applied).map(suggestionKey),
  );
  const merged: StoredSuggestion[] = [];
  const seen = new Set<string>();

  for (const suggestion of fetched) {
    const key = suggestionKey(suggestion);
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push({
      ...suggestion,
      applied: appliedKeys.has(key),
      found: markNewest ? 'latest' : previousFound.get(key),
    });
  }

  for (const suggestion of previous) {
    const key = suggestionKey(suggestion);
    if (seen.has(key)) continue;
    if (!suggestion.applied && !stillValid.has(key)) continue;
    seen.add(key);
    merged.push(markNewest ? { ...suggestion, found: 'earlier' } : suggestion);
  }

  return rankByScore(merged);
};

/**
 * Best first, by the score the Sort Suggestions plugin gave. Suggestions
 * without a score keep their order, after the scored ones.
 */
const rankByScore = <T extends Suggestion>(list: T[]): T[] =>
  [...list].sort(
    (a, b) => (b.score ?? -Infinity) - (a.score ?? -Infinity) || 0,
  );

/**
 * Earlier suggestions worth re-checking: not fetched again and not applied
 * (applied ones are kept regardless).
 */
export const suggestionsToRevalidate = (
  previous: StoredSuggestion[],
  fetched: Suggestion[],
): StoredSuggestion[] => {
  const fetchedKeys = new Set(fetched.map(suggestionKey));
  return previous.filter(
    (s) => !s.applied && !fetchedKeys.has(suggestionKey(s)),
  );
};

/** The render code for a suggestion, as the field would hold it. */
export const suggestionCode = (suggestion: Suggestion) =>
  JSON.stringify(
    suggestion.type === EntryType.Code ? suggestion.snippet : suggestion.value,
  );

/**
 * After a re-evaluation: the suggestion that should replace a filled
 * field's value, if any. Only a value that came from an applied suggestion
 * is replaced, never one typed by hand, and only by the top suggestion when
 * the re-evaluation found it, it may be autofilled and it is a different one.
 */
export const replacementFor = (
  suggestions: StoredSuggestion[],
  current: { type: EntryType; value: unknown },
): StoredSuggestion | undefined => {
  const currentCode = JSON.stringify(current.value);
  const inUse = suggestions.find(
    (s) =>
      s.applied && s.type === current.type && suggestionCode(s) === currentCode,
  );
  const [top] = suggestions;
  if (!inUse || !top) return undefined;
  if (top.found !== 'latest' || top.autofill !== true) return undefined;
  return suggestionKey(top) === suggestionKey(inUse) ? undefined : top;
};

type EntryAccess = {
  getParentId: (id: string) => string | undefined;
  getType: (id: string) => EntryType;
  getIteratorId: (mapId: string) => string | undefined;
  getTemplateValue: (id: string) => unknown;
};

const MAX_DEPTH = 20;

const findIteratorId = (entryId: string, entries: EntryAccess) => {
  let currentId: string | undefined = entryId;
  for (let depth = 0; depth < MAX_DEPTH && currentId; depth++) {
    const parentId = entries.getParentId(currentId);
    if (!parentId || entries.getType(parentId) !== EntryType.Map)
      return undefined;
    const iteratorId = entries.getIteratorId(parentId);
    if (iteratorId) return iteratorId;
    currentId = parentId;
  }
  return undefined;
};

const parseIteratorValue = (raw: unknown): any => {
  try {
    return typeof raw === 'string'
      ? JSON.parse(raw)
      : JSON.parse(JSON.stringify(raw));
  } catch {
    return undefined;
  }
};

/**
 * The kind of iterator the field sits under (nearest `#iterator` up the
 * tree), which plugins use to decide how to extract values.
 */
export const resolveIteratorStrategy = (
  entryId: string | undefined,
  entries: EntryAccess,
): IteratorStrategy | undefined => {
  if (!entryId) return undefined;
  const iteratorId = findIteratorId(entryId, entries);
  if (!iteratorId || entries.getType(iteratorId) !== EntryType.Code)
    return undefined;

  const value = parseIteratorValue(entries.getTemplateValue(iteratorId));
  if (value?.table) return 'table';
  if (value?.countoccurences !== undefined) return 'countOccurrence';
  return undefined;
};
